import React, { useState, useEffect } from 'react';
import { useCliente } from '../../hooks/useCliente';
import { useFavoritos } from '../../hooks/useFavoritos';
import { ClienteAuthModal } from './ClienteAuthModal';
import './FavoritoButton.css';

interface FavoritoButtonProps {
  imovelId: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

export const FavoritoButton: React.FC<FavoritoButtonProps> = ({ 
  imovelId, 
  className = '', 
  size = 'medium'
}) => {
  const { cliente, getClienteId, isClienteCadastrado } = useCliente();
  const clienteId = getClienteId();
  const { isFavorito, toggleFavorito, loading, favoritos } = useFavoritos(clienteId);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const favoritado = isFavorito(imovelId);
  
  // Debug: log quando favoritos mudarem
  useEffect(() => {
    console.log('🔄 Favoritos atualizados no FavoritoButton:', { 
      imovelId, 
      favoritado, 
      totalFavoritos: favoritos.length,
      clienteId 
    });
  }, [favoritos, imovelId, favoritado, clienteId]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!isClienteCadastrado()) {
      setShowAuthModal(true);
      return;
    }

    if (isToggling || loading) return;

    setIsToggling(true);
    try {
      await toggleFavorito(imovelId);
    } catch (error: any) {
      console.error('Erro ao gerenciar favorito:', error);
      // Se o erro for de cliente não encontrado, mostrar modal de autenticação
      if (error?.message?.includes('não identificado') || error?.message?.includes('não encontrado')) {
        setShowAuthModal(true);
      }
    } finally {
      setIsToggling(false);
    }
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    // Aguardar um pouco para o cliente ser salvo e o hook atualizar
    setTimeout(async () => {
      try {
        // Forçar atualização do clienteId
        const novoClienteId = getClienteId();
        if (novoClienteId) {
          console.log('✅ Cliente autenticado, adicionando favorito:', { clienteId: novoClienteId, imovelId });
          await toggleFavorito(imovelId);
        } else {
          console.error('❌ ClienteId não encontrado após autenticação');
        }
      } catch (error) {
        console.error('Erro ao adicionar favorito após autenticação:', error);
      }
    }, 1000);
  };

  return (
    <>
      <button
        className={`favorito-btn ${favoritado ? 'favoritado' : ''} ${size} ${className}`}
        onClick={handleClick}
        disabled={isToggling || loading}
        title={favoritado ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        aria-label={favoritado ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      >
        <svg 
          width={size === 'small' ? '16' : size === 'large' ? '24' : '20'} 
          height={size === 'small' ? '16' : size === 'large' ? '24' : '20'} 
          viewBox="0 0 24 24" 
          fill={favoritado ? '#000000' : 'none'} 
          stroke={favoritado ? '#000000' : 'currentColor'} 
          strokeWidth={favoritado ? '0' : '2'} 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ 
            fill: favoritado ? '#000000' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </button>

      <ClienteAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
};

